import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/ExtremeNetworks.css";
import extremeLogo from "../../assets/images/partners/extremenetworks.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const wifi7Specs = [
  { val: "46 Gbps", label: "Max Theoretical Throughput" },
  { val: "320 MHz", label: "Widest Channel Width" },
  { val: "4K QAM", label: "Highest Modulation Order" },
  { val: "6 GHz", label: "Newest Spectrum Band" },
];

const bandStats = [
  { val: "50,000+", label: "Enterprise Customers" },
  { val: "160+", label: "Countries Deployed" },
  { val: "3B+", label: "Wi-Fi Clients Supported" },
  { val: "Partner", label: "ALG Data Guard Tier" },
];

const campusProducts = [
  { icon: "📡", name: "Extreme AP5000/AP4000 — Wi-Fi 7", desc: "Tri-band Wi-Fi 7 access points with 2.5G uplink, multi-link operation (MLO), and ExtremeCloud IQ management — for high-density enterprise campus environments." },
  { icon: "🔌", name: "ExtremeSwitching X Series", desc: "Campus aggregation switches with 100GbE uplinks, intelligent PoE++ for next-gen APs and IoT devices, and ExtremeCloud IQ-driven zero-touch provisioning." },
  { icon: "🏢", name: "ExtremeSwitching 5000/4000 Series", desc: "Edge campus access switches with full PoE++ capability, VXLAN support for campus fabric, and automated configuration through ExtremeCloud IQ policy engine." },
  { icon: "🔗", name: "Fabric Connect (SPB)", desc: "Extreme's network fabric technology using Shortest Path Bridging (SPB) — zero-touch service automation, fault isolation, and network virtualization for large campus environments." },
];

const cloudCards = [
  { icon: "☁️", title: "ExtremeCloud IQ", desc: "Cloud-native network management platform providing AI-driven insights, automated configuration, and unified visibility across wired and wireless infrastructure from a single dashboard." },
  { icon: "🤖", title: "ExtremeCloud IQ CoPilot", desc: "AI-powered IT assistant for network operations — automatically identifying anomalies, predicting failures, troubleshooting connectivity issues, and recommending remediation actions." },
  { icon: "🔒", title: "ExtremeCloud Security", desc: "Zero Trust network access with dynamic policy enforcement, IoT device profiling, and automated threat response — all managed through ExtremeCloud IQ without separate NAC infrastructure." },
  { icon: "📊", title: "Extreme GTAC Analytics", desc: "Global threat analytics and network performance benchmarking — comparing your network health against 50,000+ Extreme Networks deployments worldwide for actionable improvement insights." },
  { icon: "🌐", title: "Universal Compute Platform", desc: "Extreme's edge compute and networking convergence platform — running network functions, security services, and IoT analytics as applications alongside switching and routing on a single x86 node." },
  { icon: "🔄", title: "Digital Twin Networking", desc: "Network simulation and testing environment in ExtremeCloud IQ — validate configuration changes, test policy updates, and simulate network topology changes before pushing to production." },
];

const aiCaps = [
  { icon: "🧠", title: "AI-Driven Root Cause Analysis", desc: "ExtremeCloud IQ CoPilot automatically correlates user complaints, device events, and network telemetry to identify root cause within seconds — replacing hours of manual log analysis." },
  { icon: "📊", title: "Predictive Performance Analytics", desc: "ML models predicting wireless channel congestion, power consumption anomalies, and switch capacity exhaustion 24+ hours before they impact users — enabling proactive remediation." },
  { icon: "👤", title: "User Experience Scoring", desc: "Per-client, per-AP, and per-site user experience scores tracking onboarding time, connectivity quality, and application performance — tying network health to employee productivity." },
  { icon: "🔍", title: "Wireless Spectrum Intelligence", desc: "Real-time spectrum analysis identifying interference sources — Bluetooth, microwave, rogue APs, and neighboring networks — with automated channel steering to avoid affected spectrum." },
];

const sdwanFeats = [
  { icon: "⚡", title: "Application-Aware Routing", desc: "Automatically steers application traffic to optimal WAN path based on real-time link quality measurements." },
  { icon: "🔒", title: "Integrated Security Gateway", desc: "Zone-based firewall, IPS, and URL filtering built into SD-WAN edges — no separate security appliance required." },
  { icon: "📊", title: "Real-Time WAN Visibility", desc: "Per-application, per-site bandwidth utilization and quality metrics visible from ExtremeCloud IQ." },
  { icon: "🌐", title: "Zero-Touch Branch Provisioning", desc: "Branch edges self-configure from ExtremeCloud on first boot — eliminating truck rolls for remote site deployments." },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant wireless with medical device profiling, clinical location services, and segmented IoT networks for infusion pumps, monitors, and mobile carts." },
  { icon: "🎓", name: "Education", desc: "High-density Wi-Fi for lecture halls and dorms, student/faculty network segmentation, and Chromebook onboarding — K-12 and university deployments across 6,000+ schools." },
  { icon: "🏟️", name: "Sports & Venues", desc: "Extreme Networks powers Wi-Fi at 200+ professional sports venues — Super Bowl stadiums, Olympic venues, and arena networks supporting 50,000+ concurrent users." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial Wi-Fi for factory floors with harsh RF environments, OT device segmentation, and AGV/AMR wireless connectivity with sub-10ms latency." },
  { icon: "🏪", name: "Retail", desc: "Cloud-managed switching and wireless for retail chains — centrally managed from ExtremeCloud IQ with guest Wi-Fi, location analytics, and IoT device security." },
  { icon: "🏨", name: "Hospitality", desc: "High-density guest Wi-Fi with per-room bandwidth policies, PMS integration for automatic network provisioning on check-in, and staff network segmentation." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-compliant ExtremeCloud IQ Government for federal agency Wi-Fi and switching management — supporting CAC/PIV authentication and classified network segmentation." },
  { icon: "🏢", name: "Enterprise", desc: "Large-campus switching fabric with ExtremeCloud IQ CoPilot for AI-assisted operations, Fabric Connect for service automation, and zero-trust network access policies." },
];

const deploySteps = [
  { icon: "🔍", title: "RF Site Survey", desc: "Predictive and validation RF site surveys to optimize AP placement and channel planning." },
  { icon: "📐", title: "Network Design", desc: "VLAN architecture, fabric topology, PoE budget, and SD-WAN topology design." },
  { icon: "🚀", title: "Deployment", desc: "Zero-touch provisioning through ExtremeCloud IQ — APs and switches self-configure from templates." },
  { icon: "✅", title: "Validation & Handover", desc: "Performance testing, spectrum analysis, and user acceptance testing before handover." },
];

const certs = [
  { icon: "🏆", name: "Extreme Partner", desc: "Authorized Extreme Networks resale and services partner" },
  { icon: "📡", name: "ExtremeCloud IQ Certified", desc: "ExtremeCloud IQ deployment and management specialists" },
  { icon: "🌐", name: "Campus Networking Certified", desc: "Certified campus switching and wireless deployment experts" },
  { icon: "🔒", title: "Zero Trust Certified", desc: "Extreme Network Access Control and zero-trust specialist" },
];

const faqData = [
  { q: "What is ExtremeCloud IQ and how does it manage distributed networking?", a: "ExtremeCloud IQ (IQ) is Extreme Networks' cloud-native AI-driven network management platform — a SaaS application that manages Extreme wired switches, Wi-Fi access points, SD-WAN edges, and routers from a single dashboard, regardless of location. IQ uses machine learning trained on data from 50,000+ customer networks to provide AI-powered insights: CoPilot automatically identifies the root cause of connectivity issues, predicts failures, and recommends or auto-remediates network problems. Configuration is template-based — define your network policies once, deploy to hundreds of sites automatically. ALG Data Guard implements ExtremeCloud IQ with organization-appropriate hierarchy (global, regional, site, floor) and builds your baseline configuration templates so new locations go live with zero manual configuration." },
  { q: "What is Wi-Fi 7 and when should organizations upgrade from Wi-Fi 6/6E?", a: "Wi-Fi 7 (IEEE 802.11be) delivers peak speeds of 46 Gbps through 320 MHz channels, 4096-QAM modulation, and Multi-Link Operation (MLO) — a new technology allowing a single client to simultaneously send and receive data across multiple bands and channels for dramatically lower latency and higher reliability. Practically, Wi-Fi 7 is most valuable in high-density environments (conference centers, auditoriums, open offices) where many users simultaneously stream video, use collaboration tools, or access VDI. For light-use environments, Wi-Fi 6E is still appropriate. Extreme Networks AP5000 and AP4000 are Wi-Fi 7 access points — backward compatible with all existing Wi-Fi 4/5/6 devices. ALG Data Guard assesses your current wireless environment and user density to recommend whether Wi-Fi 7 adoption delivers measurable ROI for your specific use case." },
  { q: "What is Extreme Fabric Connect (SPB) and when is it appropriate?", a: "Extreme Fabric Connect uses IEEE 802.1aq Shortest Path Bridging (SPB) to create a network fabric where any service — VLAN, VRF, multicast — can be provisioned end-to-end automatically without configuring individual switches. The fabric learns paths automatically with zero manual spanning tree or IP routing configuration at access switches. Fabric Connect is most appropriate for large campus environments (500+ switches, 100+ VLANs) where the traditional approach of configuring each switch individually is operationally burdensome. For smaller deployments, Extreme's standard VXLAN-based campus fabric or traditional VLAN configuration is simpler. ALG Data Guard recommends Fabric Connect for large enterprise, university, and healthcare campuses where operational simplification justifies the architecture investment." },
  { q: "How does Extreme Networks compare to Cisco Meraki and Aruba for campus Wi-Fi?", a: "Extreme Networks ExtremeCloud IQ, Cisco Meraki, and Aruba Central are all cloud-managed network platforms for campus Wi-Fi and switching. Extreme differentiates through its AI CoPilot — which provides more automated root cause analysis and predictive remediation than Meraki or Aruba's current AI offerings. In high-density venue deployments (stadiums, event centers), Extreme Networks has the largest installed base and most validated performance data. Extreme's Wi-Fi 7 portfolio is comprehensive. Price-wise, Extreme is generally competitive with Meraki on hardware but has a simpler perpetual licensing model for ExtremeCloud IQ relative to Meraki's all-subscription approach. ALG Data Guard provides vendor-neutral assessments comparing Extreme, Aruba, and Cisco Meraki against your requirements — including RF environment, management requirements, and total cost over 5 years." },
  { q: "How does ALG Data Guard support Extreme Networks environments?", a: "ALG Data Guard provides comprehensive Extreme Networks managed services: ExtremeCloud IQ tenant administration, AP and switch firmware management, RF performance monitoring with monthly Wi-Fi health reports, guest network and captive portal administration, zero-trust policy updates for new device types, ExtremeCloud IQ CoPilot alert review and response, and annual wireless site survey validation to detect RF environment changes. For SD-WAN environments, we manage WAN edge configuration, application policy updates, and circuit failover monitoring. We provide quarterly network health reviews with capacity planning recommendations and migration roadmaps for technology refresh cycles." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function ExtremeNetworks() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(50000, 2500, metricsInView);
  const c2 = useCounter(160, 2000, metricsInView);
  const c3 = useCounter(46, 2000, metricsInView);
  const c4 = useCounter(200, 2200, metricsInView);

  return (
    <div className="en-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={extremeLogo} alt="Extreme Networks" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Networking Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Extreme Networks</h1>
            <p className="pd-identity-desc">
              Extreme Networks is a cloud-driven enterprise networking company — delivering Wi-Fi 7 wireless, campus switching, SD-WAN, and AI-powered network management through ExtremeCloud IQ. Trusted by 50,000+ customers across healthcare, education, sports venues, and enterprise, Extreme Networks provides the intelligent networking foundation for modern digital workplaces. ALG Data Guard is an authorized Extreme Networks partner delivering design, deployment, and managed network services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="en-hero">
        <div className="en-hero__glow-c" aria-hidden="true" />
        <div className="en-hero__dot-grid" aria-hidden="true" />
        <div className="en-hero__inner">
          <div>
            <div className="en-badge en-badge--dark"><span className="en-dot" /> Extreme Networks Authorized Partner</div>
            <h2 className="en-hero__title">
              Connect Everything. <em>Intelligently.</em>
            </h2>
            <p className="en-hero__body">
              Extreme Networks delivers Wi-Fi 7 wireless, intelligent campus switching, and cloud-managed SD-WAN — all unified through ExtremeCloud IQ with AI-powered network automation that finds and fixes problems before users notice them. ALG Data Guard designs, deploys, and manages Extreme Networks environments for enterprise organizations.
            </p>
            <div className="en-hero__actions">
              <button className="en-btn en-btn--purple" onClick={() => navigate("/contact")}>
                Design My Network <ArrowIcon />
              </button>
              <button className="en-btn en-btn--ghost" onClick={() => navigate("/contact")}>
                Wi-Fi 7 Assessment
              </button>
            </div>
            <div className="en-hero__stats">
              <div><div className="en-hero__stat-val">50,000+</div><div className="en-hero__stat-label">Enterprise Customers</div></div>
              <div><div className="en-hero__stat-val">Wi-Fi 7</div><div className="en-hero__stat-label">Latest Standard</div></div>
              <div><div className="en-hero__stat-val">Partner</div><div className="en-hero__stat-label">ALG Data Guard Status</div></div>
            </div>
          </div>

          {/* WiFi Signal Visual */}
          <div className="en-wifi-visual">
            <div className="en-wifi-rings">
              <div className="en-wifi-ring en-wifi-ring--1" />
              <div className="en-wifi-ring en-wifi-ring--2" />
              <div className="en-wifi-ring en-wifi-ring--3" />
              <div className="en-wifi-ring en-wifi-ring--4" />
              <div className="en-wifi-center">📡</div>
            </div>
            <div className="en-wifi-cards">
              {[
                { label: "Standard", val: "Wi-Fi 7 (802.11be)" },
                { label: "Peak Speed", val: "46 Gbps" },
                { label: "Frequency Bands", val: "2.4 / 5 / 6 GHz" },
                { label: "ExtremeCloud IQ", val: "AI-Managed ✓" },
              ].map(c => (
                <div key={c.label} className="en-wifi-card">
                  <span className="en-wifi-card__label">{c.label}</span>
                  <span className="en-wifi-card__val">{c.val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Angle Band ── */}
      <div className="en-angle-band" aria-hidden="false">
        <div className="en-shell">
          <div className="en-angle-band__inner">
            {bandStats.map(s => (
              <div key={s.val} className="en-band-stat">
                <div className="en-band-stat__val">{s.val}</div>
                <div className="en-band-stat__label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WiFi 7 Spotlight ── */}
      <section className="en-wifi7">
        <div className="en-shell">
          <div className="en-wifi7__inner">
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80"
              alt="Wi-Fi 7 enterprise networking"
              className="en-wifi7__img"
            />
            <div>
              <div className="en-badge en-badge--dark"><span className="en-dot" /> Wi-Fi 7</div>
              <h2 className="en-wifi7__title">
                <em>Wi-Fi 7</em> — The Next Generation of Enterprise Wireless
              </h2>
              <p className="en-wifi7__body">
                Extreme Networks' Wi-Fi 7 access points — the AP5000 and AP4000 series — represent the most significant leap in wireless performance in a decade. Multi-Link Operation (MLO) fundamentally changes how devices connect, allowing simultaneous use of multiple bands for dramatically lower latency and higher reliability. For enterprise environments running Teams video, VDI, or high-density wireless deployments, Wi-Fi 7 is transformative.
              </p>
              <div className="en-wifi7__specs">
                {wifi7Specs.map(s => (
                  <div key={s.label} className="en-wifi7-spec">
                    <div className="en-wifi7-spec__val">{s.val}</div>
                    <div className="en-wifi7-spec__label">{s.label}</div>
                  </div>
                ))}
              </div>
              <button className="en-btn en-btn--purple" onClick={() => navigate("/contact")}>
                Plan My Wi-Fi 7 Upgrade <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Campus Switching ── */}
      <section className="en-campus">
        <div className="en-shell">
          <div className="en-campus__inner">
            <div>
              <div className="en-badge en-badge--purple"><span className="en-dot" /> Campus Infrastructure</div>
              <h2 className="en-campus__title">Intelligent Campus Switching & Wireless</h2>
              <p className="en-campus__body">
                Extreme Networks delivers a complete campus networking portfolio — from Wi-Fi 7 access points and PoE++ edge switches to aggregation and core switching with 400GbE uplinks. The entire portfolio is managed through ExtremeCloud IQ with zero-touch provisioning, making multi-site deployments as simple as single-site.
              </p>
              <div className="en-campus__products">
                {campusProducts.map(p => (
                  <div key={p.name} className="en-product-row">
                    <span className="en-product-row__icon">{p.icon}</span>
                    <div>
                      <div className="en-product-row__name">{p.name}</div>
                      <p className="en-product-row__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="en-btn en-btn--purple" onClick={() => navigate("/contact")}>
                  Design My Campus Network <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=900&q=80"
              alt="Enterprise campus networking"
              className="en-campus__img"
            />
          </div>
        </div>
      </section>

      {/* ── Cloud Networking ── */}
      <section className="en-cloud">
        <div className="en-shell">
          <div className="en-cloud__head">
            <div className="en-badge en-badge--dark"><span className="en-dot" /> Cloud Networking</div>
            <h2 className="en-title">ExtremeCloud IQ — The Intelligent Network Platform</h2>
            <p className="en-sub" style={{ margin:"0 auto", maxWidth:"640px" }}>ExtremeCloud IQ unifies network management, AI analytics, security, and digital twin simulation for your entire wired and wireless infrastructure.</p>
          </div>
          <div className="en-cloud__grid">
            {cloudCards.map(card => (
              <div key={card.title} className="en-cloud-card">
                <span className="en-cloud-card__icon">{card.icon}</span>
                <h3 className="en-cloud-card__title">{card.title}</h3>
                <p className="en-cloud-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Networking ── */}
      <section className="en-ai">
        <div className="en-shell">
          <div className="en-ai__inner">
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="AI network analytics"
              className="en-ai__img"
            />
            <div>
              <div className="en-badge en-badge--purple"><span className="en-dot" /> AI Network Analytics</div>
              <h2 className="en-ai__title">Network Intelligence That Acts, Not Just Alerts</h2>
              <p className="en-ai__body">
                ExtremeCloud IQ CoPilot is Extreme Networks' AI networking assistant — trained on data from 50,000+ deployments, it moves beyond reactive alerting to proactive and automated network management. CoPilot identifies root causes in seconds, not hours, and in many cases remediates issues automatically before the help desk even receives a ticket.
              </p>
              <div className="en-ai__capabilities">
                {aiCaps.map(cap => (
                  <div key={cap.title} className="en-ai-cap">
                    <span className="en-ai-cap__icon">{cap.icon}</span>
                    <div>
                      <div className="en-ai-cap__title">{cap.title}</div>
                      <p className="en-ai-cap__desc">{cap.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="en-btn en-btn--outline" onClick={() => navigate("/contact")}>
                  See CoPilot in Action <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SD-WAN ── */}
      <section className="en-sdwan">
        <div className="en-shell">
          <div className="en-sdwan__inner">
            <div>
              <div className="en-badge en-badge--dark"><span className="en-dot" /> SD-WAN</div>
              <h2 className="en-sdwan__title">Intelligent WAN for Distributed Organizations</h2>
              <p className="en-sdwan__body">
                Extreme SD-WAN delivers application-aware routing, integrated security, and zero-touch branch provisioning — all managed through ExtremeCloud IQ. Organizations with 5 to 500+ locations use Extreme SD-WAN to replace legacy MPLS circuits with flexible broadband connectivity while maintaining application performance and security.
              </p>
              <div className="en-sdwan__features">
                {sdwanFeats.map(f => (
                  <div key={f.title} className="en-sdwan-feat">
                    <span className="en-sdwan-feat__icon">{f.icon}</span>
                    <div className="en-sdwan-feat__title">{f.title}</div>
                    <p className="en-sdwan-feat__desc">{f.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="en-btn en-btn--purple" onClick={() => navigate("/contact")}>
                  Evaluate Extreme SD-WAN <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="Extreme Networks SD-WAN"
              className="en-sdwan__img"
            />
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="en-industries">
        <div className="en-shell">
          <div className="en-industries__head">
            <div className="en-badge en-badge--purple"><span className="en-dot" /> Industry Solutions</div>
            <h2 className="en-title">Extreme Networks Across Every Vertical</h2>
            <p className="en-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Extreme Networks delivers industry-specific networking solutions with validated reference architectures and compliance configurations.</p>
          </div>
          <div className="en-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="en-industry-card">
                <span className="en-industry-card__icon">{ind.icon}</span>
                <h3 className="en-industry-card__name">{ind.name}</h3>
                <p className="en-industry-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Deployment ── */}
      <section className="en-deploy">
        <div className="en-shell">
          <div className="en-deploy__head">
            <div className="en-badge en-badge--purple"><span className="en-dot" /> Deployment Process</div>
            <h2 className="en-title">How ALG Data Guard Deploys Extreme Networks</h2>
            <p className="en-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Engineer-led deployment from RF survey to validated production — with zero-touch provisioning for multi-site consistency.</p>
          </div>
          <div className="en-deploy__steps">
            {deploySteps.map(step => (
              <div key={step.title} className="en-deploy-step">
                <div className="en-deploy-step__num">{step.icon}</div>
                <h3 className="en-deploy-step__title">{step.title}</h3>
                <p className="en-deploy-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="en-metrics" ref={metricsRef}>
        <div className="en-shell">
          <div className="en-metrics__inner">
            <div><div className="en-metric__val">{c1}+</div><div className="en-metric__label">Enterprise Customers</div></div>
            <div><div className="en-metric__val">{c2}+</div><div className="en-metric__label">Countries Deployed</div></div>
            <div><div className="en-metric__val">{c3} Gbps</div><div className="en-metric__label">Wi-Fi 7 Peak Speed</div></div>
            <div><div className="en-metric__val">{c4}+</div><div className="en-metric__label">Professional Sports Venues</div></div>
          </div>
        </div>
      </section>

      {/* ── Certs ── */}
      <section className="en-certs">
        <div className="en-shell">
          <div className="en-certs__head">
            <div className="en-badge en-badge--purple"><span className="en-dot" /> Partner Credentials</div>
            <h2 className="en-title">Certified Extreme Networks Partner</h2>
            <p className="en-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard's Extreme Networks certifications validate our wireless, switching, and cloud networking expertise.</p>
          </div>
          <div className="en-certs__grid">
            {certs.map(c => (
              <div key={c.name} className="en-cert-card">
                <span className="en-cert-card__icon">{c.icon}</span>
                <div className="en-cert-card__name">{c.name}</div>
                <p className="en-cert-card__desc">{c.desc || c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="en-faq">
        <div className="en-shell">
          <div className="en-faq__head">
            <div className="en-badge en-badge--purple" style={{ margin:"0 auto 1rem" }}><span className="en-dot" /> FAQs</div>
            <h2 className="en-title">Extreme Networks Questions, Answered</h2>
            <p className="en-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Expert answers about ExtremeCloud IQ, Wi-Fi 7, campus networking, and ALG Data Guard's managed services.</p>
          </div>
          <div className="en-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`en-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="en-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="en-faq-item__q">{faq.q}</span>
                  <span className="en-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="en-faq-item__answer"><div className="en-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="en-cta">
        <div className="en-cta__inner">
          <div className="en-badge en-badge--dark" style={{ margin:"0 auto 1.2rem" }}><span className="en-dot" /> Get Started</div>
          <h2 className="en-cta__title">Build a Smarter, Faster Network with Extreme</h2>
          <p className="en-cta__sub">Whether you're upgrading to Wi-Fi 7, modernizing your campus switching fabric, deploying SD-WAN across distributed locations, or implementing AI-powered network management — ALG Data Guard's certified Extreme Networks engineers are ready to design and deliver your network transformation.</p>
          <div className="en-cta__actions">
            <button className="en-btn en-btn--purple" onClick={() => navigate("/contact")}>
              Request an Extreme Networks Assessment <ArrowIcon />
            </button>
            <button className="en-btn en-btn--ghost" onClick={() => navigate("/contact")}>
              Get Wi-Fi 7 Quote
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}